import type { ReactNode } from "react"

import { DATE_PRESETS } from "@/constants/date-presets"
import {
  BarChart3,
  Bot,
  Braces,
  CalendarClock,
  CheckSquare,
  FileText,
  Folder,
  ListChecks,
  Tag,
  Users,
} from "lucide-react"

import { DateRangePanel } from "@/components/date-range/DateRangePanel"
import { FilterPicker } from "@/components/filters/FilterPicker"

import { useRunsFilter } from "../contexts/RunsFilterContext"
import type { FilterSection } from "../types/filters"
import { RUNS_VIEW_MODE, type RunsViewMode } from "../types/viewMode"
import {
  AgentFilterSection,
  MetricsFilterSection,
  PersonaFilterSection,
  ScheduledJobFilterSection,
  StatusFilterSection,
  TagsFilterSection,
  TemplateFilterSection,
  TestCaseFilterSection,
  TestSetFilterSection,
} from "./filters"
import { RunsMetadataFilterSection } from "./filters/RunsMetadataFilterSection"

interface RunsFilterDropdownProps {
  selectedAgentId?: string | null
  filterEnabled?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  section?: FilterSection
  onSectionChange?: (section: FilterSection) => void
  /** Opens the menu in place of the toolbar's funnel; see `FilterPicker`. */
  trigger?: ReactNode
  viewMode?: RunsViewMode
}

export const RunsFilterDropdown = ({
  selectedAgentId,
  filterEnabled,
  open,
  onOpenChange,
  section,
  onSectionChange,
  trigger,
  viewMode,
}: RunsFilterDropdownProps) => {
  const filters = useRunsFilter()
  const sectionProps = { isOpen: true, onToggle: () => {}, filterEnabled }
  return (
    <FilterPicker
      label={
        viewMode === RUNS_VIEW_MODE.SIMULATIONS
          ? "Filter simulations"
          : "Filter runs"
      }
      activeCount={
        filters.totalActiveFilters - Number(Boolean(filters.searchValue.trim()))
      }
      open={open}
      onOpenChange={onOpenChange}
      section={section}
      onSectionChange={(next) => onSectionChange?.(next as FilterSection)}
      trigger={trigger}
      items={[
        {
          id: "metadata",
          label: "Metadata",
          icon: <Braces />,
          activeCount: filters.metadataFilters?.length ?? 0,
          content: <RunsMetadataFilterSection />,
        },
        {
          id: "date",
          label: "Created",
          icon: <CalendarClock />,
          activeCount: Number(
            Boolean(filters.dateRange.start || filters.dateRange.end),
          ),
          content: (
            <DateRangePanel
              value={filters.dateRange}
              onRangeChange={filters.setDateRange}
              presets={DATE_PRESETS}
              onClear={() => filters.setDateRange({ start: null, end: null })}
            />
          ),
        },
        {
          id: "status",
          label: "Status",
          icon: <CheckSquare />,
          activeCount: filters.statusFilters.length,
          content: <StatusFilterSection {...sectionProps} />,
        },
        {
          id: "agents",
          label: "Agent",
          keywords: "mutations",
          icon: <Bot />,
          disabled: Boolean(selectedAgentId),
          activeCount:
            filters.agentFilters.length + filters.mutationFilters.length,
          content: (
            <AgentFilterSection
              {...sectionProps}
              showMutations
              disabled={Boolean(selectedAgentId)}
            />
          ),
        },
        {
          id: "personas",
          label: "Persona",
          icon: <Users />,
          activeCount: filters.personaFilters.length,
          content: <PersonaFilterSection {...sectionProps} />,
        },
        {
          id: "testSets",
          label: "Test Set",
          icon: <Folder />,
          activeCount: filters.testSetFilters.length,
          content: <TestSetFilterSection {...sectionProps} />,
        },
        {
          id: "testCases",
          label: "Test Case",
          icon: <ListChecks />,
          activeCount: filters.testCaseFilters.length,
          content: <TestCaseFilterSection {...sectionProps} />,
        },
        {
          id: "metrics",
          label: "Metric",
          keywords: "score value",
          icon: <BarChart3 />,
          activeCount:
            filters.metricFilters.length + filters.metricValueFilters.length,
          content: <MetricsFilterSection {...sectionProps} />,
        },
        {
          id: "tags",
          label: "Tag",
          icon: <Tag />,
          activeCount: filters.tagFilters.length,
          content: <TagsFilterSection {...sectionProps} />,
        },
        {
          id: "templates",
          label: "Template",
          icon: <FileText />,
          activeCount: filters.templateFilters.length,
          content: <TemplateFilterSection {...sectionProps} />,
        },
        {
          id: "scheduledJobs",
          label: "Schedule",
          icon: <CalendarClock />,
          activeCount: filters.scheduledJobFilters.length,
          content: <ScheduledJobFilterSection {...sectionProps} />,
        },
      ]}
    />
  )
}
